"use client";

import React, { useState, useEffect, ChangeEvent, useRef } from "react";
import { Video, X, Loader as Loader2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Proofofimpact() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  useEffect(() => {
    const saved = localStorage.getItem("proofofimpact-video");
    if (saved) {
      setVideoURL(saved);
    }
  }, []);
  useEffect(() => {
    if (videoURL) {
      localStorage.setItem("proofofimpact-video", videoURL);
    } else {
      localStorage.removeItem("proofofimpact-video");
    }
  }, [videoURL]);
  const uploadVideoToServer = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch("/wvc/v1/upload-video", {
        method: "POST",
        body: formData
      });
      console.log('Upload response:', response);
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      const data = await response.json();
      if (data?.url) return data.url;
      if (data?.videoUrl) return data.videoUrl;
      console.warn('Upload response missing video URL');
      return null;
    } catch (error) {
      console.error("Error uploading video:", error);
      return null;
    }
  };
  const handleVideoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      const url = await uploadVideoToServer(file);
      setUploading(false);
      if (url) {
        setVideoURL(url);
      } else {
        toast.error("Failed to upload video. Please try again.");
      }
    }
  };
  return <section data-section-id="4952" id="proofofimpact" className="bg-background text-foreground py-32 md:py-48 lg:py-[200px] px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="order-2 lg:col-span-6 lg:order-2">
            <div className="w-full max-w-md mx-auto lg:ml-auto lg:mr-0">
              {uploading ? <div className="relative aspect-[9/16] w-full bg-secondary rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-4 p-12 text-center shadow-md">
                  <Loader2 className="size-12 text-primary animate-spin" />
                  <p className="font-default font-bold text-lg uppercase tracking-tight text-foreground">
                    Uploading Video...
                  </p>
                  <div className="w-full max-w-[200px] mt-2">
                    <Progress value={50} className="h-2" />
                  </div>
                </div> : !videoURL ? <div className="space-y-6">
                  <div className="relative w-full">
                    <div className="absolute -inset-4 bg-primary/30 blur-3xl rounded-lg -z-10 pointer-events-none" />
                    <div className="relative aspect-[9/16] w-full bg-black rounded-lg border-2 border-primary/80 ring-1 ring-primary/20 overflow-hidden shadow-xl transition-shadow hover:shadow-2xl">
                      <video src="https://socialcomplex.nl/wp-content/uploads/2026/08/auto-franken-video-2-week-1.mp4" autoPlay controls muted loop playsInline preload="metadata" className="w-full h-full object-cover object-center" />
                    </div>
                  </div>

                  <Button asChild className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs px-4 py-2 has-[>svg]:px-3 font-default font-bold uppercase tracking-wider border border-border rounded-lg w-full !h-[64px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:scale-[1.02] hover:shadow-lg">
                    <Link to="#contact" className="inline-flex items-center justify-center gap-2">
                      neem contact op
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div> : <div className="relative w-full">
                  <div className="absolute -inset-4 bg-primary/30 blur-3xl rounded-lg -z-10 pointer-events-none" />
                  <div className="relative aspect-[9/16] w-full bg-black rounded-lg border-2 border-primary/80 ring-1 ring-primary/20 overflow-hidden shadow-xl transition-shadow hover:shadow-2xl group flex items-center justify-center">
                    <div className="relative w-full h-full">
                      <video ref={videoRef} src={videoURL} autoPlay controls playsInline preload="metadata" loop onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} className="w-full h-full object-contain object-center" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => {
                  setVideoURL(null);
                }} className="absolute top-4 right-4 size-10 bg-background/80 backdrop-blur-md rounded-lg flex items-center justify-center text-foreground hover:text-primary hover:bg-background transition-colors z-30 p-0" aria-label="Remove video">
                      <X className="size-5" />
                    </Button>
                    <div className="absolute bottom-4 left-4 right-4 p-4 bg-background/60 backdrop-blur-sm rounded-lg border border-border/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-30">
                      <div className="flex items-center gap-3">
                        <Video className="size-4 text-primary" />
                        <span className="font-default text-[11px] tracking-[0.1em] uppercase font-bold">
                          Video Preview Mode
                        </span>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          <div className="order-1 lg:col-span-6 lg:order-1">
            <p className="text-primary text-xs tracking-[0.3em] font-bold mb-4 uppercase font-mono">
              Case Study
            </p>
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-[80px] leading-[1.05] tracking-tight text-primary uppercase mb-10">
              <span className="text-secondary-foreground">Resultaten die</span><br />opvallen.
            </h2>
            <div className="max-w-md">
              <p className="text-base leading-relaxed text-muted-foreground font-default">Voor Auto Franken verzorgden wij de complete social media-aanpak. We zetten <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofhfl" data-ast-start="7606" data-ast-end="7779">TikTok</strong> op, ontwikkelden een <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofheq" data-ast-start="7801" data-ast-end="7996">consistente contentstrategie</strong> voor <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofhdv" data-ast-start="8002" data-ast-end="8178">Instagram</strong> en <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofhd0" data-ast-start="8182" data-ast-end="8357">Facebook</strong> en zorgden met regelmatige content voor een <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofhc5" data-ast-start="8402" data-ast-end="8615">professionele en herkenbare online uitstraling</strong>. <br /><br />De focus lag hierbij niet op de hoogste views, maar op <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofh9k" data-ast-start="8684" data-ast-end="8860">conversie</strong>. Want views zijn mooi, maar <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofh8p" data-ast-start="8889" data-ast-end="9065">conversie</strong> betaalt de rekening. Daarom richtten we de content vooral op het bereiken en overtuigen van <strong className="font-bold text-foreground" data-ast-id="f:/src/pages/Home/sections/Proofofimpact.tsx|sig:eofh7u" data-ast-start="9158" data-ast-end="9343">potentiële klanten</strong>.</p>
            </div>
          </div>
        </div>

        <div className="mt-32 md:mt-40">
        </div>
      </div>
    </section>;
}